import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import keycloak from '../services/keycloak';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const stripOidcParams = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let touched = false;
  ['code', 'state', 'session_state', 'iss'].forEach((p) => {
    if (url.searchParams.has(p)) {
      url.searchParams.delete(p);
      touched = true;
    }
  });
  if (touched) {
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  }
};

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [initialized, setInitialized] = useState(false);
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (initStartedRef.current || keycloak.didInitialize) {
      return;
    }
    initStartedRef.current = true;

    keycloak
      .init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        pkceMethod: 'S256',
      })
      .then((auth) => {
        setAuthenticated(auth);
        if (auth && keycloak.tokenParsed) {
          setUserInfo({
            name: keycloak.tokenParsed.name || keycloak.tokenParsed.preferred_username || '',
            email: keycloak.tokenParsed.email || '',
          });
        }
        stripOidcParams();
        setInitialized(true);
      })
      .catch((err) => {
        console.error('Keycloak initialization failed:', err);
        setInitialized(true);
      });

    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed && keycloak.tokenParsed) {
            setUserInfo({
              name: keycloak.tokenParsed.name || keycloak.tokenParsed.preferred_username || '',
              email: keycloak.tokenParsed.email || '',
            });
          }
        })
        .catch(() => {
          console.error('Token refresh failed; user must re-login manually');
          setAuthenticated(false);
        });
    };

    keycloak.onAuthRefreshError = () => {
      console.error('Auth refresh error');
      setAuthenticated(false);
    };
  }, []);

  const logout = () => {
    keycloak.logout();
  };

  const login = () => {
    keycloak.login();
  };

  if (!initialized) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ keycloak, authenticated, userInfo, logout, login }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
